import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Check } from 'lucide-react'
import { motion } from 'motion/react'
import { useEffect } from 'react'
import type { AnswerInputProps } from '../QuestionRenderer'

function isEditableKeyboardTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false
  return Boolean(target.closest('input, textarea, select, [contenteditable="true"]'))
}

export function MultipleChoiceInput({
  question,
  value,
  onChange,
  onSubmit,
  showInlineSubmit = true,
  multiple = false,
  disabled = false,
  loadingMessage = null,
}: AnswerInputProps<string | string[]> & { multiple?: boolean }) {
  const values = Array.isArray(value) ? value : value ? [value] : []

  useEffect(() => {
    if (!showInlineSubmit || disabled) return
    function handleKey(event: KeyboardEvent) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return
      if (isEditableKeyboardTarget(event.target)) return
      const index = event.key.toUpperCase().charCodeAt(0) - 65
      const choice = question.choices[index]
      if (choice) {
        event.preventDefault()
        select(choice.value)
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  })

  function select(choiceValue: string) {
    if (disabled) return
    if (multiple) {
      const next = values.includes(choiceValue)
        ? values.filter((item) => item !== choiceValue)
        : [...values, choiceValue]
      onChange(next)
      return
    }
    onChange(choiceValue)
    if (question.settings.autoAdvance) window.setTimeout(onSubmit, 180)
  }

  return (
    <div className="flex flex-col" style={{ gap: 'var(--form-answer-gap,0.875rem)' }}>
      {disabled && loadingMessage ? (
        <div
          role="status"
          aria-label={`${question.title}選項更新中`}
          className="rounded-md border border-dashed border-slate-300 bg-white/70 px-3 py-3 text-sm font-medium text-slate-600"
        >
          {loadingMessage}
        </div>
      ) : null}
      {question.choices.map((choice, index) => {
        const selected = values.includes(choice.value)
        return (
          <button
            key={choice.id}
            type="button"
            disabled={disabled}
            onClick={() => select(choice.value)}
            className={cn(
              'flex min-h-14 w-full items-center gap-4 border p-4 text-left transition-colors',
              selected && 'border-l-4',
              disabled && 'cursor-not-allowed opacity-55',
            )}
            style={{
              background: selected
                ? 'color-mix(in srgb, var(--form-accent,#6366F1) 14%, transparent)'
                : 'color-mix(in srgb, var(--form-bg,#F8FAFC) 86%, white)',
              borderColor: selected
                ? 'var(--form-accent,#6366F1)'
                : 'color-mix(in srgb, var(--form-accent,#6366F1) 24%, transparent)',
              borderRadius: 'var(--form-radius,8px)',
              color: 'var(--form-answer,#334155)',
              fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
              fontSize: 'var(--form-body-size,1.125rem)',
              lineHeight: 'var(--form-line-height,1.55)',
            }}
          >
            <span
              className="grid size-8 shrink-0 place-items-center rounded-full text-sm font-semibold"
              style={{
                background: selected
                  ? 'var(--form-accent,#6366F1)'
                  : 'color-mix(in srgb, var(--form-accent,#6366F1) 12%, transparent)',
                color: selected ? 'var(--form-button-text,#FFFFFF)' : 'var(--form-text,#0F172A)',
              }}
            >
              {String.fromCharCode(65 + index)}
            </span>
            <span className="flex-1 font-medium">{choice.label}</span>
            {selected && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="grid size-6 place-items-center rounded-full"
                style={{
                  background: 'var(--form-accent,#6366F1)',
                  color: 'var(--form-button-text,#FFFFFF)',
                }}
              >
                <Check className="size-4" />
              </motion.span>
            )}
          </button>
        )
      })}
      {question.settings.showOther ? (
        <div
          className="flex min-h-14 w-full items-center gap-4 border p-4 text-left"
          style={{
            background: 'color-mix(in srgb, var(--form-bg,#F8FAFC) 86%, white)',
            borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 24%, transparent)',
            borderRadius: 'var(--form-radius,8px)',
          }}
        >
          <span className="text-sm font-semibold" style={{ color: 'var(--form-answer,#64748B)' }}>
            其他
          </span>
          <input
            className="min-h-10 flex-1 border-0 border-b bg-transparent outline-none"
            placeholder="請輸入其他答案"
            disabled={disabled}
            style={{
              borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 34%, transparent)',
              color: 'var(--form-answer,#334155)',
            }}
            onChange={(event) => onChange(event.target.value)}
          />
        </div>
      ) : null}
      {question.settings.randomize ? (
        <div className="text-xs" style={{ color: 'var(--form-answer,#64748B)' }}>
          公開填答時會依設定隨機排列選項。
        </div>
      ) : null}
      {multiple && showInlineSubmit && (
        <div className="flex items-center justify-between">
          <span className="text-sm" style={{ color: 'var(--form-answer,#64748B)' }}>
            已選 {values.length} 項
          </span>
          <Button
            onClick={onSubmit}
            disabled={disabled}
            style={{
              background: 'var(--form-button,#6366F1)',
              borderRadius: 'var(--form-radius,8px)',
              color: 'var(--form-button-text,#FFFFFF)',
            }}
          >
            繼續
          </Button>
        </div>
      )}
    </div>
  )
}
